/**
 * Bot Detection Types
 * TypeScript interfaces for the anti-bot protection system
 */

/**
 * Signals detected during bot analysis
 */
export type BotSignal =
  | 'webdriver'
  | 'playwright'
  | 'nightmare'
  | 'phantom'
  | 'missing_plugins'
  | 'missing_languages'
  | 'swiftshader'
  | 'screen_mismatch'
  | 'canvas_anomaly'
  | 'webgl_anomaly'
  | 'audio_anomaly'
  | 'no_mouse_movement'
  | 'robotic_keyboard'
  | 'instant_interaction'
  | 'honeypot_triggered'
  | 'honeypot_link_clicked'
  // Aggressive behavioral signals
  | 'instant_form_fill'
  | 'no_scroll_activity'
  | 'click_without_hover'
  | 'straight_line_mouse'
  | 'zero_keystroke_intervals'
  | 'no_mouse_before_click'
  | 'superhuman_typing'
  | 'programmatic_form_fill';  // Form has values but no keyboard events

/**
 * Result from bot detection analysis
 */
export interface BotDetectionResult {
  /** Score from 0-100, higher = more likely bot */
  score: number;
  /** Which detection signals were triggered */
  signals: BotSignal[];
  /** Device fingerprint hash */
  fingerprint: string;
  /** Timestamp of detection */
  timestamp: number;
  /** One-time nonce for replay protection */
  nonce: string;
}

/**
 * Mouse movement tracking data
 */
export interface MouseMovement {
  x: number;
  y: number;
  timestamp: number;
}

/**
 * Keyboard timing data
 */
export interface KeyboardTiming {
  key: string;
  timestamp: number;
  interval: number;
}

/**
 * Scroll event tracking data
 */
export interface ScrollEvent {
  timestamp: number;
  scrollY: number;
  scrollX: number;
}

/**
 * Hover event tracking data
 */
export interface HoverEvent {
  timestamp: number;
  elementId: string | null;
  duration: number;
}

/**
 * Click event tracking data
 */
export interface ClickEvent {
  timestamp: number;
  x: number;
  y: number;
  hadPriorHover: boolean;
  hadPriorMouseMove: boolean;
}

/**
 * Behavior analysis state
 */
export interface BehaviorState {
  mouseMovements: MouseMovement[];
  keyboardTimings: KeyboardTiming[];
  firstInteractionTime: number | null;
  pageLoadTime: number;
  // Enhanced tracking fields for aggressive behavioral analysis
  scrollEvents: ScrollEvent[];
  hoverEvents: HoverEvent[];
  clickEvents: ClickEvent[];
  formFillStartTime: number | null;
  formFillEndTime: number | null;
}

/**
 * Fingerprint data components
 */
export interface FingerprintData {
  canvas: string;
  webgl: string;
  audio: string;
  screen: string;
  timezone: string;
  platform: string;
}

/**
 * Bot detection context value
 */
export interface BotDetectionContextValue {
  /** Current bot score (0-100) */
  botScore: number;
  /** Whether a Turnstile challenge is required */
  turnstileRequired: boolean;
  /** Current Turnstile token if verified */
  turnstileToken: string | null;
  /** Get encrypted bot score for API requests */
  getEncryptedBotScore: () => Promise<string>;
  /** Show Turnstile challenge modal */
  showTurnstileChallenge: () => void;
  /** Handle Turnstile verification success */
  onTurnstileSuccess: (token: string) => void;
  /** Report honeypot trigger */
  reportHoneypotTrigger: () => void;
  /** Report honeypot link click */
  reportHoneypotLinkClick: () => void;
}

/**
 * Turnstile widget configuration
 */
export interface TurnstileConfig {
  siteKey: string;
  theme?: 'light' | 'dark' | 'auto';
  size?: 'normal' | 'compact';
  action?: string;
}

/**
 * API response when Turnstile is required
 */
export interface TurnstileRequiredResponse {
  error: 'TURNSTILE_REQUIRED';
  message: string;
}

/**
 * Type guard for Turnstile required response
 */
export function isTurnstileRequiredResponse(
  response: unknown
): response is TurnstileRequiredResponse {
  return (
    typeof response === 'object' &&
    response !== null &&
    'error' in response &&
    (response as TurnstileRequiredResponse).error === 'TURNSTILE_REQUIRED'
  );
}
